import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { slides as slidesData } from '../data/heroslides';
import './HeroSlideshow.css';

const HeroSlideshow = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const slideInterval = useRef(null);

  // Process slides with translations
  const slides = slidesData.map(slide => ({
    ...slide,
    title: t(slide.title, slide.defaultTitle),
    subtitle: t(slide.subtitle, slide.defaultSubtitle)
  }));

  // Auto-advance slides
  useEffect(() => {
    slideInterval.current = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
    }, 5000);

    return () => {
      if (slideInterval.current) {
        clearInterval(slideInterval.current);
      }
    };
  }, [slides.length]);

  // Handle manual navigation
  const goToSlide = (index) => {
    setCurrentSlide(index);
    // Reset the interval when manually changing slides
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
      slideInterval.current = setInterval(() => {
        setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
      }, 5000);
    }
  };

  const nextSlide = () => {
    goToSlide((currentSlide + 1) % slides.length);
  };

  const prevSlide = () => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  };

  // Touch event handlers for swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      // Swipe left, go to next slide
      nextSlide();
    }

    if (touchEnd - touchStart > 50) {
      // Swipe right, go to previous slide
      prevSlide();
    }
  };

  return (
    <div 
      className="hero-slideshow"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="slides-container">
        {slides.map((slide, index) => (
          <div 
            key={index} 
            className={`slide ${index === currentSlide ? 'active' : ''}`}
            style={slide.type === 'image' ? { 
              backgroundImage: `url(${slide.image})`,
              backgroundPosition: slide.alignment || 'center center'
            } : {}}
          >
            {slide.type === 'video' && (
              <video 
                className="slide-video" 
                autoPlay 
                muted 
                loop
                playsInline
              >
                <source src={slide.video} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
            <div className="slide-content">
              <h1 className="slide-title">{slide.title}</h1>
              <p className="slide-subtitle">{slide.subtitle}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="slideshow-controls">
        <button className="slide-arrow prev-arrow" onClick={prevSlide}>
          &#10094;
        </button>
        <div className="slide-bullets">
          {slides.map((_, index) => (
            <button 
              key={index} 
              className={`slide-bullet ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        <button className="slide-arrow next-arrow" onClick={nextSlide}>
          &#10095;
        </button>
      </div>
    </div>
  );
};

export default HeroSlideshow; 